// src/views/Admin/Locales/LocalPage/components/DeleteLocalModal/DeleteLocalModal.tsx

import React, { useState } from 'react';
import { HiOutlineExclamationTriangle, HiOutlineXMark } from 'react-icons/hi2';
import './DeleteLocalModal.scss';

interface DeleteLocalModalProps {
  isOpen: boolean;
  localName: string;
  localId: number;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteLocalModal: React.FC<DeleteLocalModalProps> = ({
  isOpen,
  localName,
  localId,
  onClose,
  onConfirm
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Normalize strings for comparison (trim whitespace and normalize unicode)
  const normalizeString = (str: string) => {
    return str.trim().normalize('NFC');
  };

  const isConfirmEnabled = normalizeString(inputValue) === normalizeString(localName);

  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await onConfirm();
      setInputValue('');
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al eliminar el local');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setInputValue('');
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isConfirmEnabled && !isDeleting) {
      handleConfirm();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="delete-modal-overlay" onClick={handleClose}>
      <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={handleClose} disabled={isDeleting}>
          <HiOutlineXMark size={20} />
        </button>

        <div className="modal-icon">
          <HiOutlineExclamationTriangle size={32} />
        </div>

        <h2>Eliminar Local</h2>
        
        <div className="warning-box">
          <p><strong>¡Esta acción es irreversible!</strong></p>
          <p>Se eliminarán permanentemente todos los datos asociados al local:</p>
          <ul>
            <li>Usuarios, permisos e invitaciones</li>
            <li>Pedidos, comandas y boletas</li>
            <li>Pagos, transacciones y DTEs</li>
            <li>Menús, categorías y productos</li>
            <li>Sesiones, turnos y estadísticas</li>
            <li>Todas las configuraciones</li>
          </ul>
        </div>

        <div className="confirmation-section">
          <p>
            Para confirmar, escribe <strong>"{localName}"</strong> a continuación:
          </p>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Escribe "${localName}" para confirmar`}
            disabled={isDeleting}
            autoFocus
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="modal-actions">
          <button 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancelar
          </button>
          <button 
            className="delete-button"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled || isDeleting}
          >
            {isDeleting ? (
              <>
                <span className="spinner" />
                Eliminando...
              </>
            ) : (
              'Eliminar Local Permanentemente'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteLocalModal;
